import { type FC, useCallback } from 'react';
import { Button, HStack, type TestProps, Text, VStack } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { requestStoreRemoderation } from '#entities/store';
import { alertSrc } from '#shared/assets';
import { useSendChatMessage } from '#shared/components/SupportChat';
import { getSafeStoreInfoRequestContext } from '#shared/lib/context';
import { useConfirm } from '#shared/lib/hooks';
import { showError, showSuccess } from '#shared/lib/toast';
import { InfoBlock } from '#shared/ui/InfoBlock';

const SUPPORT_MODERATION_MESSAGE = 'Здравствуйте! У меня есть вопрос по модерации домена моего интернет-магазина';

export const StoreIsBannedBanner: FC<TestProps> = ({
    'data-testid': dataTestId = 'StoreIsBannedBanner',
}: TestProps) => {
    const { domainBlockReasons, canRequestDomainRemoderation } = getSafeStoreInfoRequestContext();
    const sendChatMessage = useSendChatMessage();
    const { confirm, renderConfirmModal } = useConfirm();
    const { value: isRemoderationRequested, toggleOn: setIsRemoderationRequested } = useToggle(false);

    const handleSupportClick = useCallback(() => {
        void sendChatMessage(SUPPORT_MODERATION_MESSAGE);
    }, [sendChatMessage]);

    const handleRemoderationClick = useCallback(async () => {
        const isConfirmed = await confirm({
            imageSrc: alertSrc,
            title: 'Отправить магазин на проверку?',
            bodyText:
                'Если вы не исправили проблему, возможность перепроверки будет заблокирована. Убедитесь, что вы исправили проблему',
            applyText: 'Да, отправить на проверку',
            'data-testid': `${dataTestId}.RemoderationModal`,
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await requestStoreRemoderation(undefined);
            setIsRemoderationRequested();
            showSuccess('Магазин отправлен на проверку');
        } catch (error) {
            showError({ label: 'Не удалось отправить магазин на проверку' }, error);
        }
    }, [confirm, setIsRemoderationRequested, dataTestId]);

    return (
        <>
            {renderConfirmModal()}
            <InfoBlock
                type="icon"
                color="error"
                data-testid={dataTestId}
                buttons={
                    <HStack gap="8" flexWrap="wrap">
                        {canRequestDomainRemoderation && !isRemoderationRequested && (
                            <Button
                                color="contrast"
                                size="m"
                                onClick={handleRemoderationClick}
                                data-testid={`${dataTestId}.Remoderation`}
                            >
                                Отправить на перепроверку
                            </Button>
                        )}
                        <Button
                            color="contrast"
                            size="m"
                            onClick={handleSupportClick}
                            data-testid={`${dataTestId}.Support`}
                        >
                            Обратиться в поддержку
                        </Button>
                    </HStack>
                }
                caption={
                    domainBlockReasons.length > 0 ? (
                        <VStack gap="4">
                            {/* По факту Яндекс Товары всегда возвращают одну ошибку. На всякий случай обрезаем, несколько ошибок не влезут в статичную высоту */}
                            <Text role="supplementary">
                                Возможная причина блокировки: "{domainBlockReasons[0]}". Исправьте ошибки и обратитесь в
                                поддержку.
                            </Text>
                        </VStack>
                    ) : (
                        <Text role="supplementary">Обратитесь в поддержку, чтобы уточнить причину блокировки.</Text>
                    )
                }
            >
                Показы ваших товаров на Поиске ограничены.
            </InfoBlock>
        </>
    );
};
